<template>
  <div class="message-center chat-workbench" :class="{ 'is-wide': wide && !listHidden, 'list-open': listOpen, 'is-page': !(floating || externalHeader), 'is-mobile': mobile, 'mobile-detail': mobileDetail, 'is-pane-dragging': Boolean(paneGesture) }" :style="{ '--chat-rail-space': railExpanded ? '260px' : '0px' }">
    <div class="chat-container" @pointerdown="startPaneDrag" @pointermove="movePaneDrag" @pointerup="endPaneDrag" @pointercancel="cancelPaneDrag" @lostpointercapture="cancelPaneDrag">

      <!-- 左侧会话列表 -->
      <div class="chat-sidebar" :inert="(mobile ? mobileDetail : !(wide && !listHidden) && !listOpen) ? '' : null">
        <div ref="sidebarHeader" class="sidebar-header">

          <div class="sidebar-search">
            <el-input
              v-model="searchKeyword"
              :placeholder="$t('messageCenter.search')"
              prefix-icon="el-icon-search"
              size="small"
            />
          </div>
        </div>
        <div v-if="receptionEnabled" class="inbox-channels" role="tablist" :aria-label="$t('chatWorkbench.inboxChannels')">
          <button v-for="channel in ['PERSONAL', 'RECEPTION']" :key="channel" type="button" role="tab"
            :aria-selected="String(inboxChannel === channel)" :class="{ active: inboxChannel === channel }"
            @click="selectInboxChannel(channel)">
            {{ $t(channel === 'RECEPTION' ? 'chatWorkbench.receptionContacts' : 'chatWorkbench.personalContacts') }}
            <span v-if="channel === 'RECEPTION' && receptionUnread > 0" class="channel-unread">{{ receptionUnread > 99 ? '99+' : receptionUnread }}</span>
          </button>
        </div>
        <div class="conversation-list" @scroll.passive="cancelConversationLongPress(); closeConversationContextMenu(); onWorkflowInboxScroll($event)">
          <van-swipe-cell
            v-for="conv in filteredConversations"
            :key="conv.key"
            :class="[
              'conversation-swipe',
              { active: activeConv && activeConv.key === conv.key },
            ]"
            :disabled="!conv.canDelete"
          >
            <div
              :class="[
                'conversation-item',
                {
                  active: activeConv && activeConv.key === conv.key,
                  'is-pinned': isConversationPinned(conv),
                },
              ]"
              @click="onConversationClick($event, conv)"
              role="button" tabindex="0"
              @keydown.enter.self.prevent="selectConversation(conv)"
              @keydown.space.self.prevent="selectConversation(conv)"
              @keydown.shift.f10.prevent.stop="openConversationKeyboardMenu($event, conv)"
              @pointerdown="startConversationLongPress($event, conv)"
              @pointermove="moveConversationLongPress"
              @pointerup="cancelConversationLongPress"
              @pointercancel="cancelConversationLongPress"
              :aria-label="`${conv.isSelfChat ? $t('chatWorkbench.selfChat') : conv.name} · ${$t('chatWorkbench.conversationMenuHint')}`"
              @contextmenu.prevent.stop="
                openConversationContextMenu($event, conv)
              "
            >
              <div class="conv-avatar">
                <div class="conv-avatar-placeholder">
                  <i v-if="conv.kind === 'WORKFLOW_CHAT'" class="el-icon-user-solid" aria-hidden="true"></i>
                  <template v-else>{{ conv.sceneType === 'GENERAL' && !conv.isAssignedStaff ? '客' : conv.name.charAt(0) }}</template>
                </div>
              </div>
              <div class="conv-info">
                <div class="conv-top">
                  <span class="conv-name">{{ conv.isSelfChat ? $t('chatWorkbench.selfChat') : conv.name }}</span>
                  <span v-if="isConversationPinned(conv)" class="conv-pin" :aria-label="$t('chatWorkbench.pinnedConversation')" :title="$t('chatWorkbench.pinnedConversation')"><i class="el-icon-top" aria-hidden="true" /></span>
                  <span v-if="conv.kind === 'WORKFLOW_CHAT'" class="conv-kind">{{ $t(conv.isArchived ? 'workflowChat.archived' : 'workflowChat.discussion') }}</span>
                  <span
                    v-if="conv.kind === 'CUSTOMER_SERVICE' && !conv.receptionChannel"
                    class="conv-kind"
                    >{{
                      conv.sceneType === "SUPPLY_DEMAND" ? $t('agentServiceUI.supplyDemandScene') : conv.sceneType === "GENERAL" ? $t('messageCenter.generalSupport') : $t('messageCenter.orderSupport')
                    }}</span
                  >
                  <span class="conv-time">{{
                    formatConvTime(conv.lastTime)
                  }}</span>
                </div>
                <div class="conv-bottom">
                  <span class="conv-last-msg">{{ conv.isSelfChat && !conv.lastTime ? $t('chatWorkbench.selfChatHint') : conv.sceneType === 'SUPPLY_DEMAND' ? $t('agentServiceUI.supplyDemandScene') : conv.lastMessage }}</span>
                  <span v-if="conv.mentionUnread > 0" class="conv-mention">{{ $t("workflowChat.mentionedMe") }} </span>
                  <span v-if="conv.unread > 0" class="conv-unread">{{
                    conv.unread > 99 ? "99+" : conv.unread
                  }}</span>
                </div>
              </div>
            </div>
            <template #right>
              <button
                v-if="conv.canDelete && conv.kind === 'CUSTOMER_SERVICE' && conv.sceneType !== 'SUPPLY_DEMAND'"
                type="button"
                class="conversation-forget-action"
                :disabled="forgettingConversationKey === conv.key"
                :aria-busy="forgettingConversationKey === conv.key"
                :aria-label="$t('messageCenter.forgetLabel', { name: conv.name })"
                @click.stop="confirmForgetConversationMemory(conv)"
              >
                {{ forgettingConversationKey === conv.key ? $t('messageCenter.processing') : $t('messageCenter.forget') }}
              </button>
              <button
                v-if="conv.canDelete"
                type="button"
                class="conversation-delete-action"
                :disabled="deletingConversationKey === conv.key"
                :aria-busy="deletingConversationKey === conv.key"
                :aria-label="$t('messageCenter.deleteLabel', { name: conv.name })"
                @click.stop="confirmDeleteConversation(conv)"
              >
                {{ deletingConversationKey === conv.key ? $t('messageCenter.deleting') : $t('messageCenter.delete') }}
              </button>
            </template>
          </van-swipe-cell>
          <div
            v-if="filteredConversations.length === 0"
            class="no-conversation"
          >
            <span>{{ $t('messageCenter.noConversations') }}</span>
          </div>
        </div>
        <div v-if="workflowInboxError" class="workflow-inbox-error" role="status">{{ workflowInboxError }}</div>
      </div>

      <!-- 右侧聊天区域 -->
      <button v-if="!mobile && !wide && listOpen" class="list-scrim" :aria-label="$t('chatWorkbench.hideConversations')" @click="listOpen = false" />
        <div class="workbench-tools"><button v-if="!mobile" type="button" class="conversation-toggle" :aria-label="$t('chatWorkbench.conversations')" :title="$t('chatWorkbench.conversations')" :aria-expanded="String((wide && !listHidden) || listOpen)" @click="toggleConversations">
          <ChatNavigationIcon name="panel" />
        </button>
        <AgentServiceLanguagePanel v-if="languageBinding?.conversation" :mobile="mobile" ref="languagePanel" class="workbench-language" icon-only placement="right"
          :active="languageBinding.active" :conversation="languageBinding.conversation" :participant-type="languageBinding.participantType" :messages="languageBinding.messages"
          @translations="applyFormalTranslations" @preference="applyFormalLanguagePreference" />
        <span v-else-if="(!mobile || mobileDetail) && activeConv?.kind === 'WORKFLOW_CHAT'" class="group-language-placeholder" :title="$t('agentServiceUI.conversationLanguage') + ' · ' + $t('account.comingSoon')">
          <button type="button" class="conversation-toggle group-language-toggle" disabled :aria-label="$t('agentServiceUI.conversationLanguage') + ' · ' + $t('account.comingSoon')">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a17 17 0 0 1 0 18 17 17 0 0 1 0-18Z"/></svg>
          </button>
        </span>
        <button v-if="(!mobile || mobileDetail) && activeConv?.kind === 'WORKFLOW_CHAT'" type="button" class="conversation-toggle group-info-toggle" :title="$t('workflowChat.title')" :aria-label="$t('workflowChat.title')" :aria-expanded="String(groupInfoOpen)" @click="$refs.workflow.toggleInfo()">
          <ChatNavigationIcon name="group" />
        </button></div>
      <div class="chat-main" :inert="mobile && !mobileDetail ? '' : null" :style="paneStyle">
        <div v-if="mobile && mobileDetail" class="mobile-tools-space" aria-hidden="true" />

        <div v-if="openingRequest || requestError" class="workbench-status" :role="requestError ? 'alert' : 'status'">{{ requestError || $t('agentServiceUI.connecting') }}</div>
        <div v-if="!floating && !mobile && activeConv?.kind === 'WORKFLOW_CHAT'" class="chat-header workbench-page-heading">
          <span class="workbench-page-avatar" aria-hidden="true">{{ windowProfile.initial }}</span><span class="chat-title" :title="windowProfile.name">{{ windowProfile.name }}</span>
        </div>
        <div v-if="!floating && !mobile && activeConv?.kind === 'WORKFLOW_CHAT'" class="page-tools-space" aria-hidden="true" />
        <WorkflowChatWorkspace :mobile="mobile" ref="workflow"
          v-if="lastGroupId"
          v-show="activeConv && activeConv.kind === 'WORKFLOW_CHAT'"
          :key="realtimeIdentity"
          conversation-only :compact-header="true" @info-change="groupInfoOpen = $event"
          :group-id="lastGroupId"
          :visible="conversationVisible && activeConv?.kind === 'WORKFLOW_CHAT'"
          @read="workflowConversationRead"
          @private-chat="openWorkflowPrivateChat"
          @unavailable="loadWorkflowConversations()"
        />
        <template v-if="activeConv && activeConv.kind !== 'WORKFLOW_CHAT'">
          <div v-show="!floating && !mobile" class="chat-header">
            <span class="chat-title">{{ activeConv.name }}</span>
            <!-- 供需信息：显示当前咨询料号，让消息中心续聊有明确货物上下文 -->
            <span v-if="formalSupplyDemandContext" class="chat-title">
              {{ $t('agentServiceUI.supplyDemandScene') }} ·
              {{ formalSupplyDemandContext.facts.partNumber || formalSupplyDemandContext.facts.goodsName || formalSupplyDemandContext.clueId }}
            </span>
            <span
              v-if="
                activeConv.kind === 'CUSTOMER_SERVICE' &&
                activeConv.sceneType !== 'GENERAL' &&
                formalPeerPresenceResolved
              "
              class="peer-presence"
              :class="{ online: formalPeerOnline }"
              >{{ formalPeerOnline ? $t('agentServiceUI.online') : $t('agentServiceUI.offline') }}</span
            >

          </div>
          <div v-if="!floating && !mobile" class="page-tools-space" aria-hidden="true" />
          <AgentServiceHandoffBar
            :mobile="mobile"
            v-if="
              activeConv.kind === 'CUSTOMER_SERVICE' &&
              displayFormalConversation
            "
            :policy="formalControlPolicy"
            :agent-processing="formalState.agentProcessing"
            :control-changing="
              isConversationControlChanging(activeFormalConversationId)
            "
            :realtime-state="formalState.realtimeState"
            @change-control="changeFormalControl"
          />
          <div
            v-if="activeConv.kind === 'CUSTOMER_SERVICE' && formalState.error"
            class="formal-sync-error"
            role="status"
          >
            {{ localizeNotice(formalState.error) }}
          </div>
          <AgentServiceMessageList
            :mobile="mobile"
            :key="activeConv.key"
            :viewer-role="activeConv.receptionChannel ? 'RECEPTION' : ''"
            v-if="activeConv.kind === 'CUSTOMER_SERVICE'"
            ref="formalMessageList"
            :active="conversationVisible && Boolean(activeConv)"
            preserve-read-position
            :messages="formalState.messages"
            :translations="formalMessageTranslations"
            :language-preference="formalLanguagePreference"
            :translation-mode="formalTranslationMode"
            :conversation="displayFormalConversation"
            :staff-name="displayFormalConversation.assignedStaffDisplayName"
            :current-user-id="currentUserId"
            :agent-processing="formalState.agentProcessing"
            :has-more="formalState.hasMore"
            :loading-older="formalState.loadingOlder"
            :loading="formalState.loading"
            :can-select-suggestion="
              formalControlPolicy.participantType === 'CUSTOMER' &&
              canSendActiveConversation &&
              !formalState.agentProcessing
            "
            :new-message-count="activeFormalNewMessageCount"
            @load-older="loadOlderFormalMessages"
            @retry="retryFormalMessage"
            @select-suggestion="sendFormalSuggestion"
            @history-anchor-visited="clearFormalNewMessageCount"
          />
          <div
            v-else-if="activeConv.kind !== 'CUSTOMER_SERVICE'"
            class="chat-messages" v-stable-chat-width
            ref="chatMessages"
          >
            <div data-chat-width-canvas><div v-for="(msg, index) in activeConv.messages" :key="index" :data-chat-message="msg.id || index">
              <div
                v-if="shouldShowLegacyTimeDivider(msg, index)"
                class="message-time-divider"
              >
                {{ formatMessageTimeDivider(msg.time) }}
              </div>
              <div
                :class="[
                  'message-row',
                  msg.isSelf ? 'message-self' : 'message-other',
                ]"
              >
                <div
                  :class="[
                    'msg-bubble',
                    msg.isSelf ? 'bubble-self' : 'bubble-other',
                  ]"
                >
                  <div class="msg-text">{{ msg.content }}</div>
                </div>
              </div>
            </div>
            </div>
          </div>
          <AgentServiceComposer :enter-to-send="!mobile" :mobile="mobile" ref="composer" v-model="inputText"
            :show-controls="activeConv.kind === 'CUSTOMER_SERVICE'"
            :disabled="!canSendActiveConversation" :can-submit="canSendActiveConversation && Boolean(inputText.trim())"
            :sending="activeConv.kind === 'CUSTOMER_SERVICE' && formalState.sending"
            :policy="formalControlPolicy" :control-state="displayFormalConversation?.controlState || ''"
            :control-changing="Boolean(formalState.controlChanging)" :translation-mode="formalTranslationMode"
            @mode-change="changeFormalMode" @update:translation-mode="changeFormalTranslationMode" @send="sendMessage" />
        </template>
        <div v-if="!activeConv" class="chat-placeholder">
          <div class="placeholder-content">
            <i class="el-icon-chat-dot-round placeholder-icon"></i>
            <p>{{ $t('messageCenter.chooseConversation') }}</p>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="conversationContextMenu.visible"
      v-body-portal
      class="conversation-context-menu"
      role="menu"
      :style="{
        left: `${conversationContextMenu.x}px`,
        top: `${conversationContextMenu.y}px`,
        width: `${conversationMenuWidth()}px`,
        zIndex: conversationContextMenu.layer,
      }"
      @click.stop
      @contextmenu.prevent
    >
      <button ref="conversationContextPin" type="button" role="menuitem" class="conversation-context-pin" @click="toggleContextConversationPin">
        <i class="el-icon-top" aria-hidden="true"></i>
        <span>{{ $t(isConversationPinned(conversationContextMenu.conversation) ? 'chatWorkbench.unpinConversation' : 'chatWorkbench.pinConversation') }}</span>
      </button>
      <button
        v-if="
          conversationContextMenu.conversation &&
          conversationContextMenu.conversation.canDelete &&
          conversationContextMenu.conversation.kind === 'CUSTOMER_SERVICE' &&
          conversationContextMenu.conversation.sceneType !== 'SUPPLY_DEMAND'
        "
        type="button"
        role="menuitem"
        class="conversation-context-forget"
        :disabled="Boolean(forgettingConversationKey)"
        :aria-busy="
          conversationContextMenu.conversation &&
          forgettingConversationKey === conversationContextMenu.conversation.key
        "
        @click="forgetContextConversation"
      >
        <i class="el-icon-refresh-left" aria-hidden="true"></i>
        <span>{{ $t('messageCenter.forgetTitle') }}</span>
      </button>
      <button
        v-if="conversationContextMenu.conversation && conversationContextMenu.conversation.canDelete"
        ref="conversationContextDelete"
        type="button"
        role="menuitem"
        class="conversation-context-delete"
        :disabled="Boolean(deletingConversationKey)"
        :aria-busy="
          conversationContextMenu.conversation &&
          deletingConversationKey === conversationContextMenu.conversation.key
        "
        @click="deleteContextConversation"
      >
        <i class="el-icon-delete" aria-hidden="true"></i>
        <span>{{ $t('messageCenter.deleteTitle') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { getLegacySequenceMessages, markLegacyMessagesRead } from "@/api/realtimeRecovery";
import { legacyChatAttempt, confirmLegacyChatAttempt, applyLegacyResourceEvent } from "@/services/legacy-chat-attempt";
import { ChatRealtimeClient } from "@/services/chat-realtime";
import { catchUpLegacyMessages } from "@/services/legacy-chat-sync";
import {
  getChatUsers,
  getChatMessages,
  sendChatMessage,
  markAsRead,
  deleteLegacyChatConversation,
} from "@/api/index";
import {
  deleteAgentServiceConversation,
  forgetAgentServiceConversationMemory,
  listAgentServiceConversations,
  resolveAgentServiceConversation,
} from "@/api/agentService";
import ChatNavigationIcon from "./ChatNavigationIcon.vue";
import { chatPaneDrag } from "@/services/chat-pane-navigation";
import stableChatWidth from "@/services/chat-stable-width";
import AgentServiceComposer from "@/components/agent-service/AgentServiceComposer.vue";
import AgentServiceHandoffBar from "@/components/agent-service/AgentServiceHandoffBar.vue";
import WorkflowChatWorkspace from "@/components/workflow-chat/WorkflowChatWorkspace.vue";
import workflowChatInbox from "@/mixins/workflowChatInbox";
import { compareInboxConversations } from "@/services/workflow-chat-inbox";
import { selfChatConversation, chatPinStorageKey, readChatPins, writeChatPins, toggleChatPin, sortPinnedConversations } from "@/services/chat-inbox-preferences";
import AgentServiceMessageList from "@/components/agent-service/AgentServiceMessageList.vue";
import AgentServiceLanguagePanel from "@/components/agent-service/AgentServiceLanguagePanel.vue";
import AgentServiceModeSwitch from "@/components/agent-service/AgentServiceModeSwitch.vue";
import AgentServiceTranslationSwitch from "@/components/agent-service/AgentServiceTranslationSwitch.vue";
import AgentServiceSendButton from "@/components/agent-service/AgentServiceSendButton.vue";
import { adaptAgentServiceConversationView } from "@/services/agent-service-contract";
import { AgentServiceRealtimeClient } from "@/services/agent-service-realtime";
import {
  mapAgentServiceInboxConversation,
  requireAgentServiceSuccess,
  resolveAgentServiceControlPolicy,
  resolveAgentServiceCurrentUserId,
} from "@/services/agent-service-state";

export default {
  name: "ChatWorkbench",
  directives: { stableChatWidth, bodyPortal: {
    inserted(el) { document.body.appendChild(el); },
    unbind(el) { el.remove(); },
  } },
  props: { externalHeader: { type: Boolean, default: false }, mobile: { type: Boolean, default: false }, visible: { type: Boolean, default: true }, floating: { type: Boolean, default: false } },
  mixins: [workflowChatInbox],
  components: {
    ChatNavigationIcon,
    AgentServiceComposer,
    WorkflowChatWorkspace,
    AgentServiceHandoffBar,
    AgentServiceMessageList,
    AgentServiceLanguagePanel,
    AgentServiceModeSwitch,
    AgentServiceTranslationSwitch,
    AgentServiceSendButton,
  },
  data() {
    return {
      mobileDetail: false, paneGesture: null, paneOffset: 0,
      searchKeyword: "",
      receptionEnabled: false, inboxChannel: 'PERSONAL',
      wide: false, listOpen: false, listHidden: false, groupInfoOpen: false, lastGroupId: "", requestError: "", openingRequest: false,
      requestEpoch: 0, drafts: {}, readPositions: {}, externalContexts: {},
      activeConv: null,
      inputText: "",
      legacyConversations: [],
      formalConversations: [],
      formalMessageTranslations: {},
      formalLanguagePreference: null,
      formalLanguageSnapshots: {},
      formalNewMessageCounts: {},
      inboxRealtime: null,
      legacyRealtime: null,
      loadingMsg: false,
      realtimeClient: null,
      deletingConversationKey: "",
      forgettingConversationKey: "",
      formalPeerOnline: false,
      formalPeerPresenceResolved: false,
      formalPeerPresenceByConversationId: {},
      formalListRequestSequence: 0,
      conversationContextMenu: {
        visible: false,
        x: 0,
        y: 0,
        conversation: null,
      },
    };
  },
  computed: {
    conversationVisible() { return this.visible && (!this.mobile || this.mobileDetail); },
    paneStyle() { return this.mobile && this.paneGesture ? { transform: `translate3d(${this.paneOffset}px,0,0)` } : null; },
    expandedList() { return this.wide && !this.listHidden; },
    railExpanded() { return this.expandedList || this.listOpen; },
    languageBinding() {
      return (!this.mobile || this.mobileDetail) && this.activeConv?.kind === 'CUSTOMER_SERVICE' ? { active: this.conversationVisible && Boolean(this.displayFormalConversation), conversation: this.displayFormalConversation,
        participantType: this.formalControlPolicy.participantType, messages: this.formalState.messages } : null;
    },
    windowProfile() {
      if (this.mobile && !this.mobileDetail) return { name: this.$t('chatWorkbench.title'), initial: '聊' };
      const conv = this.activeConv;
      if (conv?.isSelfChat) return { name: this.$t('chatWorkbench.selfChat'), initial: '我' };
      const formal = conv?.kind === 'CUSTOMER_SERVICE' ? this.displayFormalConversation : null;
      // 总客服是平台身份，负责人仅用于人工接管，不能成为平台头像或名称。
      if (conv?.kind === 'CUSTOMER_SERVICE' && conv.sceneType === 'GENERAL'
        && !conv.isAssignedStaff) {
        return { name: this.$t('agentServiceUI.generalTitle'), initial: '客' };
      }
      const name = formal && this.formalControlPolicy.participantType === 'CUSTOMER'
        ? formal.assignedStaffDisplayName || conv.name : conv?.name;
      return { name: name || this.$t('chatWorkbench.title'), initial: (name || this.$t('chatWorkbench.title')).trim().charAt(0) };
    },
    formalNamespace() {
      return this.activeConv?.sceneType === 'GENERAL' ? 'generalAgentService'
        : this.activeConv?.sceneType === 'SUPPLY_DEMAND' ? 'supplyDemandAgentService' : 'agentService';
    },
    realtimeIdentity() {
      return `${this.currentUserId}:${String(this.$store.state.user?.userInfo?.token || "")}`;
    },
    currentUserId() {
      return resolveAgentServiceCurrentUserId(this.$store.state.user || {});
    },
    formalState() {
      return this.$store.state[this.formalNamespace];
    },
    formalConversation() {
      return this.formalState.conversation;
    },
    /** 首次打开即沿用收件箱已有会话事实；正式详情到达后再静默替换。 */
    displayFormalConversation() {
      if (
        String(this.formalConversation?.conversationId || "") ===
        String(this.activeFormalConversationId || "")
      ) {
        return this.formalConversation;
      }
      return this.activeConv?.formalConversation || null;
    },
    formalTranslationMode() {
      return this.$store.state.agentServiceUi.translationMode;
    },
    activeFormalConversationId() {
      return this.activeConv?.kind === "CUSTOMER_SERVICE"
        ? this.activeConv.conversationId
        : "";
    },
    activeFormalNewMessageCount() {
      return Number(
        this.formalNewMessageCounts[this.activeFormalConversationId] || 0,
      );
    },
    formalControlPolicy() {
      if (!this.displayFormalConversation) {
        return {
          canSend: false,
          canRequestHuman: false,
          canAcceptHuman: false,
          canRejectHandoff: false,
          canReleaseToAi: false,
          statusText: this.$t("messageCenter.unavailable"),
        };
      }
      return resolveAgentServiceControlPolicy(
        this.displayFormalConversation,
        this.currentUserId,
        this.activeConv?.receptionChannel ? 'RECEPTION' : '',
      );
    },
    canSendActiveConversation() {
      if (!this.conversationVisible || this.openingRequest) return false;
      return this.activeConv?.kind !== "CUSTOMER_SERVICE"
        ? Boolean(this.activeConv)
        : this.formalControlPolicy.canSend &&
            String(this.formalConversation?.conversationId || "") ===
              String(this.activeFormalConversationId || "") &&
            !this.formalState.sending;
    },
    /** 消息中心续聊明确使用当前可见的最新货物卡片，重试仍保留原引用。 */
    formalSupplyDemandContext() {
      if (this.activeConv?.sceneType !== 'SUPPLY_DEMAND') return null;
      const message = [...(this.formalState.messages || [])].reverse().find(item =>
        item.conversationId === this.activeFormalConversationId && item.messageType === 'SUPPLY_DEMAND_CARD');
      try {
        const card = JSON.parse(message?.content || '');
        return card.publisherUserId === this.displayFormalConversation?.assignedStaffUserId ? card : this.externalContexts[this.activeFormalConversationId] || null;
      } catch { return this.externalContexts[this.activeFormalConversationId] || null; }
    },
    selfConversation() { return this.legacyConversations.find(row => row.isSelfChat && String(row.userId) === String(this.currentUserId)) || selfChatConversation(this.currentUserId, this.legacyConversations, key => this.$t(key)); },
    inboxPins() {
      return this.$store.state.chatWorkbench?.inboxPinsByUser?.[this.currentUserId] ?? (this.selfConversation ? [this.selfConversation.key] : []);
    },
    conversations() {
      const self = this.selfConversation;
      const legacy = this.legacyConversations.filter(row => !self || String(row.userId) !== String(this.currentUserId));
      return sortPinnedConversations([...this.formalConversations, ...legacy, ...(self ? [self] : []), ...this.workflowConversations], this.inboxPins, compareInboxConversations);
    },
    channelConversations() {
      return this.receptionEnabled ? this.conversations.filter(conv => Boolean(conv.receptionChannel) === (this.inboxChannel === 'RECEPTION')) : this.conversations;
    },
    receptionUnread() { return this.formalConversations.filter(conv => conv.receptionChannel).reduce((sum, conv) => sum + (Number(conv.unread) || 0), 0); },
    filteredConversations() {
      if (!this.searchKeyword.trim()) {
        return this.channelConversations;
      }
      const keyword = this.searchKeyword.trim().toLowerCase();
      return this.channelConversations.filter((conv) =>
        conv.name.toLowerCase().includes(keyword),
      );
    },
  },
  watch: {
    mobileDetail(value) { this.syncVisibleState(); this.$emit('mobile-pane-change', value); if (value) this.$nextTick(this.restoreReadingPosition); },
    mobile() { this.mobileDetail = false; this.paneGesture = null; this.paneOffset = 0; },
    railExpanded: { immediate: true, handler(value) { this.$emit('layout-change', value); } },
    floating() { this.$emit('layout-change', this.railExpanded); },
    visible: { immediate: true, handler(value) {
      this.syncVisibleState();
      if (value) this.$nextTick(() => this.restoreReadingPosition());
      else { this.cancelConversationLongPress(); this.closeConversationContextMenu(); this.captureReadingPosition(); }
    } },
    'activeConv.key'() { this.syncVisibleState(); this.$nextTick(() => this.$emit('layout-change', this.railExpanded)); },
    windowProfile: { immediate: true, handler(value) { this.$emit('profile-change', value); } },
    realtimeIdentity() {
      this.cancelConversationLongPress(); this._longPressClick = null; this.closeConversationContextMenu(); this.restoreInboxPins();
      this.stopUnreadPolling(); this.stopMessagePolling();
      this.disconnectFormalRealtime();
      this.mobileDetail = false; this.activeConv = null; this.drafts = {}; this.readPositions = {}; this.externalContexts = {}; this.lastGroupId = ""; this.requestEpoch++;
      this.legacyConversations = []; this.formalConversations = [];
      this.receptionEnabled = false; this.inboxChannel = 'PERSONAL';
      this.ensureSelfConversation();
      this.startUnreadPolling();
    },
  },
  mounted() {
    this.ensureSelfConversation();
    this.restoreInboxPins();
    this._workspaceObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width || 0;
      cancelAnimationFrame(this._layoutFrame);
      this._layoutFrame = requestAnimationFrame(() => {
        if (width >= 800) this.wide = true;
        else if (width <= 740) { this.wide = false; this.listHidden = false; }
        if (this.wide) this.listOpen = false;
      });
    }) : null;
    this._workspaceObserver?.observe(this.$el);
    this.loadChatUsers();
    this.loadAgentServiceConversations();
    this.startUnreadPolling();
    document.addEventListener("click", this.closeConversationContextMenu);
    document.addEventListener("contextmenu", this.closeConversationContextMenu);
    document.addEventListener("keydown", this.handleContextMenuKeydown);
    window.addEventListener("resize", this.closeConversationContextMenu);
    window.addEventListener("scroll", this.closeConversationContextMenu, true);
    window.addEventListener("scroll", this.cancelConversationLongPress, true);
    window.addEventListener("storage", this.onInboxPinStorage);
  },
  beforeDestroy() {
    this.cancelConversationLongPress();
    this.relocateChrome({});
    this.requestEpoch++;
    this._workspaceObserver?.disconnect(); cancelAnimationFrame(this._layoutFrame);
    this.formalListRequestSequence += 1;
    this.stopUnreadPolling();
    this.stopMessagePolling();
    this.disconnectFormalRealtime();
    document.removeEventListener("click", this.closeConversationContextMenu);
    document.removeEventListener(
      "contextmenu",
      this.closeConversationContextMenu,
    );
    document.removeEventListener("keydown", this.handleContextMenuKeydown);
    window.removeEventListener("resize", this.closeConversationContextMenu);
    window.removeEventListener("storage", this.onInboxPinStorage);
    window.removeEventListener("scroll", this.cancelConversationLongPress, true);
    window.removeEventListener(
      "scroll",
      this.closeConversationContextMenu,
      true,
    );
    for (const namespace of ['agentService', 'generalAgentService', 'supplyDemandAgentService']) this.$store.commit(namespace + '/setVisible', false);
  },
  methods: {
    async selectInboxChannel(channel) {
      if (!this.receptionEnabled || !['PERSONAL', 'RECEPTION'].includes(channel) || channel === this.inboxChannel) return;
      this.cancelConversationLongPress(); this.closeConversationContextMenu();
      this.captureReadingPosition();
      if (this.activeConv?.key) this.$set(this.drafts, this.activeConv.key, this.inputText);
      this.inboxChannel = channel;
      if (this.mobile) { this.showMobileInbox(); return; }
      const next = this.filteredConversations[0];
      if (next) await this.selectConversation(next);
      else {
        this.rememberFormalLanguageSnapshot(); this.stopMessagePolling(); this.disconnectFormalRealtime();
        this.activeConv = null; this.inputText = ''; this.syncVisibleState();
      }
    },
    ensureSelfConversation() {
      const self = selfChatConversation(this.currentUserId, this.legacyConversations, key => this.$t(key));
      if (!self) return;
      const index = this.legacyConversations.findIndex(row => String(row.userId) === String(this.currentUserId));
      if (index < 0) this.legacyConversations.push(self);
      else if (!this.legacyConversations[index].isSelfChat) this.$set(this.legacyConversations, index, self);
    },
    inboxStorage() { try { return window.localStorage; } catch { return null; } },
    restoreInboxPins() {
      if (!this.currentUserId) return;
      this.$store.commit('chatWorkbench/setInboxPins', { userId: this.currentUserId,
        pins: readChatPins(this.inboxStorage(), this.currentUserId, this.selfConversation?.key) });
    },
    onInboxPinStorage(event) {
      if (event.key === null || event.key === chatPinStorageKey(this.currentUserId)) this.restoreInboxPins();
    },
    isConversationPinned(conversation) { return Boolean(conversation && this.inboxPins.includes(conversation.key)); },
    toggleContextConversationPin() {
      const conv = this.conversationContextMenu.conversation;
      if (!conv?.key || !this.currentUserId) return;
      const pins = toggleChatPin(this.inboxPins, conv.key);
      this.$store.commit('chatWorkbench/setInboxPins', { userId: this.currentUserId, pins });
      writeChatPins(this.inboxStorage(), this.currentUserId, pins);
      this.closeConversationContextMenu();
    },
    onConversationClick(event, conversation) {
      if (this._longPressClick?.key === conversation.key && (this._conversationPress || Date.now() < this._longPressClick.until) && event.detail !== 0) {
        event.preventDefault(); event.stopPropagation(); this._longPressClick = null; return;
      }
      this._longPressClick = null;
      this.selectConversation(conversation);
    },
    openConversationKeyboardMenu(event, conversation) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.openConversationContextMenu({ clientX: rect.left + 16, clientY: rect.top + 16 }, conversation);
    },
    startConversationLongPress(event, conversation) {
      this.cancelConversationLongPress();
      if (!this.mobile || event.pointerType === 'mouse' || event.isPrimary === false) return;
      const { clientX: x, clientY: y, pointerId: id } = event;
      this._conversationPress = { x, y, id };
      this._conversationPressTimer = setTimeout(() => {
        this._conversationPressTimer = null;
        this._longPressClick = { key: conversation.key, until: Date.now() + 1500 };
        this.openConversationContextMenu({ clientX: x, clientY: y }, conversation);
      }, 500);
    },
    moveConversationLongPress(event) {
      const press = this._conversationPress;
      if (press && (Math.hypot(event.clientX - press.x, event.clientY - press.y) > 10 || event.pointerId !== press.id)) this.cancelConversationLongPress();
    },
    cancelConversationLongPress() {
      if (this._longPressClick) this._longPressClick.until = Date.now() + 1500;
      clearTimeout(this._conversationPressTimer); this._conversationPressTimer = null; this._conversationPress = null;
    },
    /** 收起详情保留会话、草稿与在途回复；可见性门禁负责暂停已读上报。 */
    showMobileInbox() { this.$refs?.composer?.$refs.input?.blur(); this.$refs?.workflow?.$refs.composer?.$refs.input?.blur(); this.captureReadingPosition(); if (this.activeConv?.key) this.$set(this.drafts, this.activeConv.key, this.inputText); this.mobileDetail = false; this.paneGesture = null; this.paneOffset = 0; },
    /** 左边缘右滑仅改变视图，不触碰消息、控制权或网络请求。 */
    startPaneDrag(event) {
      if (!this.mobile || !this.mobileDetail || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (event.target.closest?.('input,textarea,button,a,[contenteditable="true"]')) return;
      const left = this.$el.getBoundingClientRect().left;
      if (event.clientX - left > 28) return;
      this.paneGesture = { id: event.pointerId, x: event.clientX, y: event.clientY }; this.paneOffset = 0;
    },
    movePaneDrag(event) {
      const start = this.paneGesture;
      if (!start || event.pointerId !== start.id) return;
      const drag = chatPaneDrag(start, { x: event.clientX, y: event.clientY }, this.$el.clientWidth);
      if (drag.cancelled) { this.cancelPaneDrag(); return; }
      this.paneOffset = drag.offset;
      if (drag.offset > 8) { event.currentTarget.setPointerCapture?.(event.pointerId); if (event.cancelable) event.preventDefault(); }
    },
    endPaneDrag(event) {
      const start = this.paneGesture;
      if (!start || event.pointerId !== start.id) return;
      const drag = chatPaneDrag(start, { x: event.clientX, y: event.clientY }, this.$el.clientWidth);
      this.cancelPaneDrag(); if (!drag.cancelled && drag.dismiss) this.showMobileInbox();
    },
    cancelPaneDrag() { this.paneGesture = null; this.paneOffset = 0; },
    relocateChrome({ searchTarget }) {
      const search = this.$refs.sidebarHeader;
      if (search && !this._searchHome) this._searchHome = search.parentNode;
      if (search && (searchTarget || this._searchHome) && search.parentNode !== (searchTarget || this._searchHome))
        (searchTarget || this._searchHome).prepend(search);
      this.$refs.languagePanel?.positionPopover();
    },
    toggleConversations() { if (this.mobile) { if (this.mobileDetail) this.showMobileInbox(); else if (this.activeConv) this.mobileDetail = true; return; } if (this.wide) this.listHidden = !this.listHidden; else this.listOpen = !this.listOpen; },
    syncVisibleState() {
      for (const namespace of ['agentService', 'generalAgentService', 'supplyDemandAgentService'])
        this.$store.commit(namespace + '/setVisible', Boolean(this.conversationVisible && this.activeConv?.kind === 'CUSTOMER_SERVICE' && namespace === this.formalNamespace));
      if (this.conversationVisible && this.activeConv?.kind === 'CUSTOMER_SERVICE' && this.formalState.conversation)
        this.$store.dispatch(this.formalNamespace + '/markVisibleMessagesRead').catch(() => {});
    },
    captureReadingPosition() {
      const key = this.activeConv?.key;
      if (!key) return;
      const box = this.activeConv.kind === 'CUSTOMER_SERVICE' ? this.$refs?.formalMessageList?.$refs.messageList : this.$refs?.chatMessages;
      if (box?.clientHeight) this.$set(this.readPositions, key, { top: box.scrollTop, followLatest: Boolean(this.$refs?.formalMessageList?.followLatest) });
      this.$refs?.formalMessageList?.captureReadPosition();
      this.$refs?.workflow?.saveDraft();
    },
    restoreReadingPosition() {
      this.$refs?.languagePanel?.positionPopover();
      const key = this.activeConv?.key;
      const box = this.activeConv?.kind === 'CUSTOMER_SERVICE' ? this.$refs?.formalMessageList?.$refs.messageList : this.$refs?.chatMessages;
      if (box && Object.prototype.hasOwnProperty.call(this.readPositions, key)) {
        const saved = this.readPositions[key];
        if (this.$refs?.formalMessageList) this.$refs.formalMessageList.followLatest = saved.followLatest;
        box.scrollTop = saved.top;
      }
      this.$refs?.formalMessageList?.restoreReadPosition();
    },
    /** 消息提示打开完整收件箱；清除旧搜索条件，桌面联系人栏保持展开。 */
    expandConversations() { if (this.mobile) { this.showMobileInbox(); return; } this.searchKeyword = ""; this.listHidden = false; this.listOpen = !this.wide; },
    async openRequest(request) {
      if (!request) return;
      const epoch = ++this.requestEpoch;
      this.requestError = ''; this.openingRequest = true;
      try {
        if (request.kind === 'INBOX') {
          await Promise.all([this.loadChatUsers(), this.loadAgentServiceConversations(), this.loadWorkflowConversations()]);
          if (epoch !== this.requestEpoch) return;
          if (this.mobile) { this.showMobileInbox(); return; }
          const next = this.channelConversations.find(row => Number(row.unread) > 0)
            || this.channelConversations.find(row => row.key === this.activeConv?.key) || this.channelConversations[0];
          if (next) await this.selectConversation(next);
          return;
        }
        if (request.kind === 'PRIVATE') { await this.openWorkflowPrivateChat(request.member); return; }
        if (request.kind === 'WORKFLOW') {
          await this.loadWorkflowConversations();
          if (epoch !== this.requestEpoch) return;
          const conv = this.workflowConversations.find(row => String(row.groupId) === String(request.groupId));
          await this.selectConversation(conv || { key: 'workflow-chat:' + request.groupId, kind: 'WORKFLOW_CHAT', groupId: String(request.groupId), name: request.title || this.$t('workflowChat.discussion'), unread: 0 });
          return;
        }
        let conversation;
        if (request.kind === 'SUPPLY_DEMAND') {
          conversation = request.session?.conversation;
          if (conversation?.conversationId && request.session?.context)
            this.$set(this.externalContexts, conversation.conversationId, request.session.context);
        } else {
          const scene = request.kind === 'GENERAL' ? { sceneType: 'GENERAL' }
            : { sceneType: request.sceneType, ...(request.sceneType === 'PRODUCT' ? { goodsCode: String(request.goodsCode) } : { orderNum: String(request.orderNum) }) };
          conversation = adaptAgentServiceConversationView(requireAgentServiceSuccess(await resolveAgentServiceConversation(scene)));
        }
        if (epoch !== this.requestEpoch || !conversation?.conversationId) return;
        const mapped = { ...mapAgentServiceInboxConversation(conversation, this.currentUserId), formalConversation: conversation };
        if (!this.formalConversations.some(row => row.key === mapped.key)) this.formalConversations.push(mapped);
        await this.selectConversation(mapped);
      } catch (error) {
        if (epoch === this.requestEpoch) this.requestError = this.localizeNotice(error.message || this.$t('messageCenter.conversationsFailed'));
      } finally { if (epoch === this.requestEpoch) this.openingRequest = false; }
    },
    async closeFloating() {
      this.captureReadingPosition();
      if (this.activeConv?.kind === 'CUSTOMER_SERVICE' && this.formalControlPolicy.participantType === 'CUSTOMER'
          && this.displayFormalConversation?.controlState === 'AI_ENABLED' && (this.formalState.agentProcessing || this.formalState.sending))
        await this.$store.dispatch(this.formalNamespace + '/cancelPendingReply').catch(() => {});
    },
    conversationMenuWidth() {
      return this.$store.state.locale?.uiLocale === "en-US" ? 280 : 136;
    },
    localizeNotice(value) {
      // 只翻译已登记的系统提示；聊天正文和未知服务端说明保持原文。
      const keys = {
        订单客服操作失败: "operationFailed",
        "会话已不存在，请重新打开客服窗口。": "conversationMissing",
        客服会话尚未建立: "conversationNotReady",
        "消息正在发送，请稍候": "sendingBusy",
        "客服控制状态正在更新，请稍候": "controlBusy",
        消息不能为空: "emptyMessage",
        当前会话状态不能发送消息: "cannotSend",
        客服控制状态更新失败: "controlUpdateFailed",
        网络繁忙请稍后再试: "networkBusy",
        订单客服会话加载失败: "conversationsFailed",
        客服会话加载失败: "conversationsFailed",
        消息发送失败: "sendFailed",
        "客服消息同步失败，请稍后重试。": "syncFailed",
      };
      return typeof value === "string" && Object.hasOwn(keys, value)
        ? this.$t(`messageCenter.${keys[value]}`)
        : value;
    },
    applyFormalTranslations(translations) {
      this.formalMessageTranslations = translations || {};
      this.rememberFormalLanguageSnapshot();
    },
    applyFormalLanguagePreference(preference) {
      this.formalLanguagePreference = preference || null;
      this.rememberFormalLanguageSnapshot();
    },
    rememberFormalLanguageSnapshot() {
      const conversationId = this.activeFormalConversationId;
      if (!conversationId) return;
      this.$set(this.formalLanguageSnapshots, conversationId, {
        translations: this.formalMessageTranslations,
        preference: this.formalLanguagePreference,
      });
    },
    restoreFormalLanguageSnapshot(conversationId) {
      const snapshot = this.formalLanguageSnapshots[conversationId];
      this.formalMessageTranslations = snapshot?.translations || {};
      this.formalLanguagePreference = snapshot?.preference || null;
    },
    clearFormalNewMessageCount() {
      const conversationId = this.activeFormalConversationId;
      if (conversationId)
        this.$set(this.formalNewMessageCounts, conversationId, 0);
    },
    changeFormalTranslationMode(mode) {
      this.$store.commit("agentServiceUi/setTranslationMode", mode);
    },
    /** 在鼠标位置打开会话菜单，并确保菜单不会超出当前视口。 */
    openConversationContextMenu(event, conversation) {
      if (!conversation?.key) return;
      if (this.mobile) this._longPressClick = { key: conversation.key, until: Date.now() + 1500 };
      this.cancelConversationLongPress();
      const menuWidth = this.conversationMenuWidth();
      const menuHeight = 8 + 44 * (1 + (conversation.canDelete ? 1 : 0) + (conversation.canDelete && conversation.kind === 'CUSTOMER_SERVICE' && conversation.sceneType !== 'SUPPLY_DEMAND' ? 1 : 0));
      const viewportGap = 8;
      let layer = 2100, ancestor = this.$el;
      while (ancestor?.nodeType === 1) {
        layer = Math.max(layer, Number(window.getComputedStyle?.(ancestor)?.zIndex) || 0);
        ancestor = ancestor.parentElement;
      }
      const x = Math.max(viewportGap, Math.min(
        Math.max(viewportGap, event.clientX),
        window.innerWidth - menuWidth - viewportGap,
      ));
      const y = Math.max(viewportGap, Math.min(
        Math.max(viewportGap, event.clientY),
        window.innerHeight - menuHeight - viewportGap,
      ));
      this.conversationContextMenu = {
        visible: true,
        x,
        y,
        conversation,
        layer: layer + 1,
      };
      this.$nextTick(() => this.$refs.conversationContextPin?.focus({ preventScroll: true }));
    },
    closeConversationContextMenu() {
      if (!this.conversationContextMenu.visible) return;
      this.conversationContextMenu = {
        visible: false,
        x: 0,
        y: 0,
        conversation: null,
      };
    },
    handleContextMenuKeydown(event) {
      if (event.key === "Escape") {
        this.closeConversationContextMenu();
      }
    },
    /** 右键菜单只提供入口，实际删除继续复用统一确认与接口逻辑。 */
    async deleteContextConversation() {
      const conversation = this.conversationContextMenu.conversation;
      this.closeConversationContextMenu();
      await this.confirmDeleteConversation(conversation);
    },
    /** 右键菜单与滑动入口复用同一主动遗忘确认链路。 */
    async forgetContextConversation() {
      const conversation = this.conversationContextMenu.conversation;
      this.closeConversationContextMenu();
      await this.confirmForgetConversationMemory(conversation);
    },
    /** 只遗忘Agent咨询记忆，不删除聊天窗口、消息或业务会话。 */
    async confirmForgetConversationMemory(conv) {
      if (
        !conv?.canDelete ||
        conv.kind !== "CUSTOMER_SERVICE" ||
        this.forgettingConversationKey
      )
        return;
      try {
        await this.$confirm(
          this.$t("messageCenter.forgetConfirm"),
          this.$t("messageCenter.forgetTitle"),
          {
            confirmButtonText: this.$t("messageCenter.confirmForget"),
            cancelButtonText: this.$t("messageCenter.cancel"),
            confirmButtonClass: "el-button--danger",
            type: "warning",
          },
        );
      } catch (error) {
        return;
      }

      this.forgettingConversationKey = conv.key;
      try {
        const response = await forgetAgentServiceConversationMemory(
          conv.conversationId,
        );
        requireAgentServiceSuccess(response);
        this.$message.success(this.$t("messageCenter.forgotten"));
      } catch (error) {
        const message =
          error.response?.data?.msg || error.message || this.$t("messageCenter.forgetFailed");
        this.$message.error(this.localizeNotice(message));
      } finally {
        this.forgettingConversationKey = "";
      }
    },
    /** 确认后删除双方的完整会话，不做仅前端隐藏。 */
    async confirmDeleteConversation(conv) {
      if (!conv?.canDelete || this.deletingConversationKey) return;
      try {
        await this.$confirm(
          this.$t("messageCenter.deleteConfirm", { name: conv.name }),
          this.$t("messageCenter.deleteTitle"),
          {
            confirmButtonText: this.$t("messageCenter.delete"),
            cancelButtonText: this.$t("messageCenter.cancel"),
            confirmButtonClass: "el-button--danger",
            type: "warning",
          },
        );
      } catch (error) {
        return;
      }

      this.deletingConversationKey = conv.key;
      try {
        if (conv.kind === "CUSTOMER_SERVICE") {
          const response = await deleteAgentServiceConversation(
            conv.conversationId,
          );
          requireAgentServiceSuccess(response);
        } else {
          await deleteLegacyChatConversation(conv.sessionId);
        }
        this.removeDeletedConversation(conv);
        this.$message.success(this.$t("messageCenter.deleted"));
      } catch (error) {
        const message =
          error.response?.data?.msg || error.message || this.$t("messageCenter.deleteFailed");
        this.$message.error(this.localizeNotice(message));
      } finally {
        this.deletingConversationKey = "";
      }
    },
    /** 后端确认删除后，再同步移除本地列表和当前会话状态。 */
    removeDeletedConversation(conv) {
      if (conv.kind === "CUSTOMER_SERVICE") {
        this.formalConversations = this.formalConversations.filter(
          (item) => item.key !== conv.key,
        );
      } else {
        this.legacyConversations = this.legacyConversations.filter(
          (item) => item.key !== conv.key,
        );
      }
      if (this.activeConv?.key === conv.key) {
        const namespace = this.formalNamespace;
        this.stopMessagePolling();
        this.disconnectFormalRealtime();
        this.activeConv = null;
        this.inputText = "";
        this.$store.commit(namespace + "/reset");
      }
      this.updateTotalUnread();
    },
    // 处理企业微信授权登录回调
    async goWxLogin() {
      const code = this.$route.query.code;
      const corpId = this.$route.query.corpId === "true";
      if (!code) {
        this.$myMessage.error(this.$t("messageCenter.authMissing"));
        return;
      }
      try {
        const res = await wxLogin(code, corpId);
        if (res.code !== 200) {
          this.$myMessage.error(this.localizeNotice(res.msg || this.$t("messageCenter.wecomLoginFailed")));
          return;
        }
        this.$store.commit("user/setUserInfo", res.data);
        const response = await getUserInfoDetail();
        if (response.code === 200 && response.data) {
          this.$store.commit(
            "user/setUserDetails",
            response.data.userInfo ?? {},
          );
        }
        const newQuery = { ...this.$route.query };
        delete newQuery.code;
        delete newQuery.corpId;
        this.$router.replace({ query: newQuery });
        await this.initInspectionPage();
      } catch (error) {
        const errorMsg =
          error.response?.data?.msg || error.message || this.$t("messageCenter.wecomLoginError");
        this.$myMessage.error(this.localizeNotice(errorMsg));
      }
    },
    loadChatUsers() {
      if (!this.currentUserId) return;
      this.ensureSelfConversation();
      const identity = this.realtimeIdentity;
      return getChatUsers(this.currentUserId)
        .then((res) => {
          if (identity !== this.realtimeIdentity) return;
          if (res.code === 200 && res.data) {
            const oldConvs = this.legacyConversations;
            const nextConversations = (res.data || []).map((user) => {
              const existing = oldConvs.find(
                (c) => String(c.userId) === String(user.userId),
              );
              const mapped = {
                kind: "LEGACY",
                key: `legacy:${String(user.sessionId || user.userId)}`,
                userId: user.userId,
                sessionId: user.sessionId,
                name: user.userName || this.$t("messageCenter.unknownUser"),
                phone: user.phone || "",
                isUnread: user.isUnread || false,
                unread: user.unreadCount || 0,
                lastTime: user.lastMessageTime
                  ? new Date(user.lastMessageTime)
                  : null,
                messages: existing ? existing.messages : [],
                lastMessageId: existing ? existing.lastMessageId || 0 : 0,
                lastMessage: user.lastMessage || "",
                // 旧聊天没有可靠的客户/接待角色事实，不能沿用参与者即可删除的旧权限。
                canDelete: false,
              };
              return String(user.userId) === String(this.currentUserId)
                ? selfChatConversation(this.currentUserId, [mapped], key => this.$t(key)) : mapped;
            });
            nextConversations.push(...oldConvs.filter(previous => previous.localOnly && !nextConversations.some(row => String(row.userId) === String(previous.userId))));
            this.legacyConversations = nextConversations;
            const self = selfChatConversation(this.currentUserId, nextConversations, key => this.$t(key));
            if (self && !nextConversations.some(row => row.key === self.key)) nextConversations.push(self);

            if (this.activeConv?.kind === "LEGACY") {
              const activeId = String(this.activeConv.userId);
              const activeSessionId = String(this.activeConv.sessionId);
              const syncedActive = nextConversations.find(
                (conv) =>
                  String(conv.userId) === activeId ||
                  String(conv.sessionId) === activeSessionId,
              );
              this.activeConv = syncedActive || null;

              if (!this.activeConv) {
                this.stopMessagePolling();
              }
            }
            this.updateTotalUnread();
          }
        })
        .catch((err) => {
          console.error("获取聊天用户列表失败:", err);
        });
    },
    /** 将新客服事实表中的客户和固定业务员会话并入消息中心。 */
    loadAgentServiceConversations() {
      if (!this.currentUserId) return;
      const requestSequence = this.formalListRequestSequence + 1;
      this.formalListRequestSequence = requestSequence;
      return listAgentServiceConversations(50, { inbox: true })
        .then((res) => {
          if (requestSequence !== this.formalListRequestSequence) return;
          if (res.code !== 200) return;
          // 数组仅兼容旧接口/旧部署；正式分组与接待资格由服务端给出。
          const grouped = !Array.isArray(res.data) && res.data;
          if (!Array.isArray(res.data) && (!grouped || !Array.isArray(grouped.personalConversations) || !Array.isArray(grouped.receptionConversations))) return;
          this.receptionEnabled = grouped?.receptionEnabled === true;
          if (!this.receptionEnabled) this.inboxChannel = 'PERSONAL';
          const sources = Array.isArray(res.data) ? res.data.map(source => ({ source, receptionChannel: false }))
            : [...grouped.personalConversations.map(source => ({ source, receptionChannel: false })),
              ...(this.receptionEnabled ? grouped.receptionConversations.map(source => ({ source, receptionChannel: true })) : [])];
          const previousByKey = new Map(
            this.formalConversations.map((item) => [item.key, item]),
          );
          this.formalConversations = sources.map(({ source, receptionChannel }) => {
            const conversation = adaptAgentServiceConversationView(source);
            const mapped = mapAgentServiceInboxConversation(
              conversation,
              this.currentUserId,
              this.conversationVisible && Boolean(this.activeConv?.receptionChannel) === receptionChannel ? this.activeFormalConversationId : "",
              receptionChannel ? 'RECEPTION' : '',
            );
            const previous = previousByKey.get(mapped.key);
            const messageSequenceUnchanged =
              previous &&
              previous.formalConversation?.lastSequence ===
                conversation.lastSequence;
            return {
              ...mapped,
              receptionChannel,
              // 控制状态、AI开关和已读游标也会更新conversation.updatedAt；
              // 只有消息序号发生变化时，才允许会话以新时间重新排序。
              lastTime: messageSequenceUnchanged
                ? previous.lastTime
                : mapped.lastTime,
              formalConversation: conversation,
            };
          });
          if (this.activeConv?.kind === "CUSTOMER_SERVICE") {
            const previousActive = previousByKey.get(this.activeConv.key);
            const synced = this.formalConversations.find(
              (item) => item.key === this.activeConv.key,
            );
            const activeMessageSequenceChanged = Boolean(
              synced &&
                Number(synced.formalConversation?.lastSequence || 0) >
                  Number(previousActive?.formalConversation?.lastSequence || 0),
            );
            if (synced) {
              this.activeConv = synced;
              if (
                String(this.formalConversation?.conversationId || "") ===
                String(synced.conversationId)
              ) {
                this.$store.commit(
                  this.formalNamespace + "/setConversation",
                  synced.formalConversation,
                );
              }
            }
            // 静态会话继续展示缓存；只有服务端消息序号增长才补读消息正文。
            if (!synced || activeMessageSequenceChanged)
              this.refreshActiveFormalMessages().catch(() => {});
          }
          this.updateTotalUnread();
        })
        .catch((error) => {
          if (requestSequence !== this.formalListRequestSequence) return;
          console.error("获取订单客服会话列表失败:", error);
        });
    },
    updateTotalUnread() {
      return this.$store.dispatch("user/refreshUnreadCount")
        .catch(() => { console.warn("消息未读汇总刷新失败"); });
    },
    /** 补读当前正式会话后立刻推进服务端游标，避免下一轮未读回弹。 */
    async refreshActiveFormalMessages() {
      const conversationId = this.activeFormalConversationId;
      if (!conversationId) return;
      try {
        await this.$store.dispatch(this.formalNamespace + "/refreshConversation");
        if (conversationId !== this.activeFormalConversationId) return;
        await this.$store.dispatch(this.formalNamespace + "/refreshMessages", {
          markRead: false,
        });
        if (conversationId !== this.activeFormalConversationId) return;
        await this.$store.dispatch(this.formalNamespace + "/markVisibleMessagesRead");
      } catch (error) {
        if (!this.reportFormalSyncError(error, conversationId)) throw error;
      }
    },
    /** REST与WebSocket补读统一失败语义；分页缺席不代表删除，旧连接不能清空新会话。 */
    reportFormalSyncError(error, conversationId) {
      if (conversationId !== this.activeFormalConversationId) return true;
      const missing =
        error.response?.status === 404 ||
        error.code === 404 ||
        error.errorCode === "CONVERSATION_NOT_FOUND" ||
        error.response?.data?.errorCode === "CONVERSATION_NOT_FOUND";
      if (missing) {
        const namespace = this.formalNamespace;
        this.disconnectFormalRealtime();
        this.stopMessagePolling();
        this.activeConv = null;
        this.$store.commit(namespace + "/reset");
        return true;
      }
      this.$store.commit(
        this.formalNamespace + "/setError",
        error.message || this.$t("messageCenter.syncFailed"),
      );
      return false;
    },
    /** 公共SVG模式切换复用当前会话的正式控制接口。 */
    changeFormalMode(mode) {
      if (this.formalControlPolicy.participantType !== "CUSTOMER") return;
      this.changeFormalControl(mode === "agent" ? "enable" : "request");
    },
    /** 当前会话的控制更新期间，阻止重复交接操作。 */
    isConversationControlChanging(conversationId) {
      const stableConversationId = String(conversationId || "");
      if (!stableConversationId) return false;
      return Boolean(
        this.formalState.controlChanging &&
          String(this.formalState.controlChangingConversationId || "") === stableConversationId,
      );
    },
    async selectConversation(conv) {
      if (this.receptionEnabled) this.inboxChannel = conv.receptionChannel ? 'RECEPTION' : 'PERSONAL';
      if (this.mobile) this.mobileDetail = true;
      if (this.activeConv?.key === conv.key) { this.listOpen = false; return; }
      this.captureReadingPosition();
      if (this.activeConv?.key) this.$set(this.drafts, this.activeConv.key, this.inputText);
      this.inputText = this.drafts[conv.key] || '';
      this.listOpen = false;
      if (this.activeFormalConversationId)
        this.$set(
          this.formalNewMessageCounts,
          this.activeFormalConversationId,
          0,
        );
      this.rememberFormalLanguageSnapshot();
      this.stopMessagePolling();
      this.disconnectFormalRealtime();
      this.activeConv = conv;

      if (conv.kind === "WORKFLOW_CHAT") {
        this.lastGroupId = String(conv.groupId);
        return;
      }

      if (conv.kind === "CUSTOMER_SERVICE") {
        const conversationId = String(conv.conversationId || "");
        this.restoreFormalLanguageSnapshot(conversationId);
        this.$set(
          this.formalNewMessageCounts,
          conversationId,
          Math.max(0, Number(conv.unread || 0)),
        );
        if (
          Object.prototype.hasOwnProperty.call(
            this.formalPeerPresenceByConversationId,
            conversationId,
          )
        ) {
          this.formalPeerOnline = Boolean(
            this.formalPeerPresenceByConversationId[conversationId],
          );
          this.formalPeerPresenceResolved = true;
        } else {
          this.formalPeerPresenceResolved = false;
        }
        try {
          const openedConversation = await this.$store.dispatch(
            this.formalNamespace + "/openConversation",
              conv.receptionChannel ? { conversationId: conv.conversationId, viewerRole: 'RECEPTION' } : conv.conversationId,
          );
          if (
            !openedConversation ||
            this.activeConv?.key !== conv.key ||
            String(this.formalConversation?.conversationId || "") !==
              String(conv.conversationId)
          ) {
            return;
          }
          if (this.conversationVisible) { conv.unread = 0; conv.isUnread = false; }
          this.connectFormalRealtime();
          this.updateTotalUnread();
          this.$nextTick(() => this.restoreReadingPosition());
        } catch (error) {
          console.error("打开订单客服会话失败:", error);
        }
        return;
      }

      if (conv.isUnread && this.conversationVisible) {
        markAsRead(conv.sessionId, this.currentUserId)
          .then(() => {
            conv.unread = 0;
            conv.isUnread = false;
            this.updateTotalUnread();
            this.loadMessages(conv);
          })
          .catch(() => {
            this.loadMessages(conv);
          });
      } else {
        this.loadMessages(conv);
      }
      this.startMessagePolling();
    },
    loadMessages(conv) {
      if (!conv.sessionId) return;
      this.loadingMsg = true;

      const identity = this.realtimeIdentity;
      return getChatMessages(conv.sessionId)
        .then((res) => {
          if (identity !== this.realtimeIdentity || this.activeConv?.sessionId !== conv.sessionId) return;
          if (res.code === 200 && res.data) {
            const list = Array.isArray(res.data)
              ? res.data
              : res.data.list || [];
            conv.messages = list.map((msg) => ({
              id: msg.id,
              content: msg.content,
              isSelf: String(msg.senderUserId) === this.currentUserId,
              time: msg.sendTime || msg.createTime,
            }));
            conv.lastMessageId = list.reduce(
              (maxId, msg) => Math.max(maxId, Number(msg.id || 0) || 0),
              0,
            );
          }
          this.$nextTick(() => { this.restoreReadingPosition(); });
        })
        .catch((err) => {
          console.error("加载聊天记录失败:", err);
        })
        .finally(() => {
          this.loadingMsg = false;
        });
    },
    async sendMessage() {
      if (!this.inputText.trim() || !this.activeConv) return;

      if (this.activeConv.kind === "CUSTOMER_SERVICE") {
        if (this.formalState.sending) return;
        const content = this.inputText.trim();
        this.inputText = "";
        try {
          await this.$store.dispatch(this.formalNamespace + "/sendMessage", {
            content,
            currentUserId: this.currentUserId,
            ...(this.activeConv.sceneType === 'SUPPLY_DEMAND' ? { contextId: this.formalSupplyDemandContext?.contextId } : {}),
          });
          await this.loadAgentServiceConversations();
        } catch (error) {
          console.error("订单客服消息发送失败:", error);
        }
        return;
      }

      const content = this.inputText.trim();
      const conv = this.activeConv;
      const receiverUserId = conv.userId;

      this.inputText = "";

      const params = {
        sessionId: conv.sessionId,
        sessionType: 1,
        clientMessageId: legacyChatAttempt(conv, content, receiverUserId),
        senderUserId: this.currentUserId,
        receiverUserIds: JSON.stringify([receiverUserId]),
        messageType: 1,
        content: content,
        status: 1,
      };

      sendChatMessage(params)
        .then((res) => {
          if (res.code === 200) {
            if (!conv.messages.some(m => res.data?.id && String(m.id) === String(res.data.id))) conv.messages.push({
              id: res.data?.id || null,
              content: content,
              isSelf: true,
              time: new Date(),
            });
            confirmLegacyChatAttempt(conv, params.clientMessageId);
            this.$nextTick(() => {
              this.scrollToBottom();
            });
          }
        })
        .catch((err) => {
          console.error("发送消息失败:", err);
        });
    },
    // 兼容原页面生命周期方法名，内部已改为共享WebSocket订阅。
    startUnreadPolling() {
      this.stopUnreadPolling();
      this.inboxRealtime = new ChatRealtimeClient({
        token: String(this.$store.state.user?.userInfo?.token || ""),
        subscriptions: [{ channel: "legacy-chat", resourceId: "self" }, { channel: "customer-service", resourceId: "self" }],
        onRefresh: async () => { await Promise.all([this.loadChatUsers(), this.loadAgentServiceConversations()]); },
      });
      this.inboxRealtime.connect();
    },
    stopUnreadPolling() {
      this.inboxRealtime?.close(); this.inboxRealtime = null;
    },
    startMessagePolling() {
      this.stopMessagePolling();
      const sessionId = this.activeConv?.sessionId;
      if (!sessionId) return;
      let recoveryAfter = null;
      const client = new ChatRealtimeClient({
        token: String(this.$store.state.user?.userInfo?.token || ""),
        subscriptions: [{ channel: "legacy-chat", resourceId: "self" }],
        onEvent: event => {
          applyLegacyResourceEvent(this.activeConv, event);
          if (String(event.resourceId) === String(sessionId) && Number(event.sequence) > 0)
            recoveryAfter = Math.min(recoveryAfter ?? Number(event.sequence), Number(event.sequence) - 1);
        },
        onRefresh: async full => {
          const fromId = full ? 0 : recoveryAfter ?? undefined;
          recoveryAfter = null;
          await catchUpLegacyMessages({ fromId,
            sessionId, currentUserId: this.currentUserId, getMessages: getChatMessages, markRead: markAsRead,
            getSequenceMessages: getLegacySequenceMessages, markSpecific: markLegacyMessagesRead,
            canMarkRead: () => this.conversationVisible && !document.hidden,
            onRead: () => this.updateTotalUnread(),
            getCurrent: () => this.legacyRealtime === client ? this.activeConv : null, onMore: after => { recoveryAfter = after; client.refresh(); },
          });
          if (this.conversationVisible && String(this.activeConv?.sessionId) === String(sessionId)) this.restoreReadingPosition();
        },
      });
      this.legacyRealtime = client;
      client.connect();
    },
    stopMessagePolling() {
      this.legacyRealtime?.close(); this.legacyRealtime = null;
    },
    /** 为消息中心当前正式会话建立认证、订阅和断线重连通道。 */
    connectFormalRealtime() {
      const token = String(this.$store.state.user?.userInfo?.token || "");
      if (!token || !this.formalConversation?.conversationId) return;
      const conversationId = String(this.formalConversation.conversationId);
      const isCurrentConnection = () =>
        this.realtimeClient === realtimeClient &&
        this.activeFormalConversationId === conversationId;
      const realtimeClient = new AgentServiceRealtimeClient({
        token,
        conversationId,
        onRefresh: async () => {
          if (!isCurrentConnection()) return;
          await this.$store.dispatch(this.formalNamespace + "/refreshConversation");
          if (isCurrentConnection()) await this.$store.dispatch(this.formalNamespace + "/refreshMessages");
        },
        onNotification: (notification) => {
          if (!isCurrentConnection()) return;
          return this.$store
            .dispatch(this.formalNamespace + "/handleNotification", notification)
            .then(() => {
              if (isCurrentConnection()) {
                return this.loadAgentServiceConversations();
              }
              return null;
            })
            .catch((error) => {
              if (isCurrentConnection())
                this.reportFormalSyncError(error, conversationId);
            });
        },
        onStateChange: (state) => {
          if (!isCurrentConnection()) return;
          this.$store.commit(this.formalNamespace + "/setRealtimeState", state);
          if (state !== "CONNECTED") this.formalPeerPresenceResolved = false;
        },
        onPresenceChange: ({ userId, online }) => {
          if (!isCurrentConnection()) return;
          const peerUserId =
            this.formalControlPolicy.participantType === "CUSTOMER"
              ? this.formalConversation?.assignedStaffUserId
              : this.formalConversation?.customerUserId;
          if (String(userId) === String(peerUserId || "")) {
            this.formalPeerOnline = Boolean(online);
            this.formalPeerPresenceResolved = true;
            this.$set(
              this.formalPeerPresenceByConversationId,
              conversationId,
              Boolean(online),
            );
          }
        },
        onError: (error) => {
          if (!isCurrentConnection()) return;
          console.error("订单客服实时连接异常:", error);
        },
      });
      this.realtimeClient = realtimeClient;
      realtimeClient.connect();
    },
    disconnectFormalRealtime() {
      if (this.realtimeClient) this.realtimeClient.close();
      this.realtimeClient = null;
      this.formalPeerOnline = false;
      this.formalPeerPresenceResolved = false;
    },
    changeFormalControl(action) {
      if (this.isConversationControlChanging(this.activeFormalConversationId)) {
        return;
      }
      this.$store
        .dispatch(this.formalNamespace + "/changeControl", { action })
        .then(() => this.loadAgentServiceConversations())
        .catch((error) => {
          console.error("订单客服控制状态更新失败:", error);
        });
    },
    loadOlderFormalMessages() {
      this.$store.dispatch(this.formalNamespace + "/loadOlderMessages");
    },
    retryFormalMessage(message) {
      this.$store
        .dispatch(this.formalNamespace + "/sendMessage", {
          content: message.content,
          currentUserId: this.currentUserId,
          clientMessageId: message.clientMessageId,
          ...(message.contextId ? { contextId: message.contextId } : {}),
        })
        .catch((error) => {
          console.error("订单客服消息重试失败:", error);
        });
    },
    /** 客户点击推荐问句直接发消息；业务员查看同一消息时不得冒充客户提问。 */
    sendFormalSuggestion(content) {
      if (
        this.formalControlPolicy.participantType !== "CUSTOMER" ||
        !this.canSendActiveConversation ||
        this.formalState.agentProcessing
      )
        return;
      this.$store
        .dispatch(this.formalNamespace + "/sendMessage", {
          content,
          currentUserId: this.currentUserId,
          ...(this.activeConv?.sceneType === 'SUPPLY_DEMAND' ? { contextId: this.formalSupplyDemandContext?.contextId } : {}),
        })
        .catch((error) => {
          this.$myMessage.error(this.localizeNotice(error.message || this.$t("messageCenter.sendFailed")));
        });
    },
    scrollToBottom() {
      const el = this.$refs.chatMessages;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },
    shouldShowLegacyTimeDivider(message, index) {
      if (index === 0) return true;
      const current = new Date(message.time).getTime();
      const previous = new Date(
        this.activeConv?.messages?.[index - 1]?.time,
      ).getTime();
      if (!Number.isFinite(current) || !Number.isFinite(previous)) return false;
      return (
        current - previous >= 5 * 60 * 1000 ||
        new Date(current).toDateString() !== new Date(previous).toDateString()
      );
    },
    formatMessageTimeDivider(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      const now = new Date();
      const time = date.toLocaleTimeString(this.$store.state.locale?.uiLocale || "zh-CN", {
        hour: "2-digit",
        minute: "2-digit",
      });
      if (date.toDateString() === now.toDateString()) return time;
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      if (date.toDateString() === yesterday.toDateString())
        return this.$t("messageCenter.yesterday", { time });
      if (now.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000)
        return `${date.toLocaleDateString(this.$store.state.locale?.uiLocale || "zh-CN", {
          weekday: "long",
        })} ${time}`;
      return `${date.toLocaleDateString(this.$store.state.locale?.uiLocale || "zh-CN", { month: "short", day: "numeric" })} ${time}`;
    },
    formatConvTime(date) {
      if (!date) return "";
      const now = new Date();
      const d = new Date(date);
      const diffMs = now - d;
      const diffMin = Math.floor(diffMs / (1000 * 60));
      const diffHour = Math.floor(diffMs / (1000 * 60 * 60));

      if (Number.isNaN(d.getTime())) return "";
      if (diffMin < 1) return this.$t("messageCenter.justNow");
      if (diffMin < 60) return this.$t("messageCenter.minutesAgo", { count: diffMin });
      if (diffHour < 24) return this.$t("messageCenter.hoursAgo", { count: diffHour });

      const month = d.getMonth() + 1;
      const day = d.getDate();
      return `${month}/${day}`;
    },
  },
};
</script>

<style lang="less" scoped>
.sidebar-header { box-sizing: border-box; width: 100%; padding: 16px 12px 12px; background: #fff; }
.message-center {
  &.chat-workbench {
    position: relative;
    width: 100%;
    flex: 1 1 auto;
    overflow: hidden;
    background: #fff;
    border-radius: var(--chat-radius, 12px);
    .chat-container { position: relative; border: 0; border-radius: var(--chat-radius, 12px); }
    ::v-deep .el-input__inner { border-radius: var(--chat-radius, 12px); }
    .chat-sidebar { width: 260px; flex: 0 0 260px; border-radius: var(--chat-radius, 12px); }
    .workbench-status { position: absolute; z-index: 4; top: 48px; left: 12px; right: 12px; padding: 8px 12px; background: #fff; color: #667085; font-size: 13px; box-shadow: 0 4px 12px rgba(15,23,42,.08); }
    .workbench-tools { position: absolute; z-index: 9; top: 4px; left: calc(var(--chat-rail-space, 0px) + 6px); display: flex; gap: 2px; transition: left 240ms cubic-bezier(.2,.8,.2,1); }
    .conversation-toggle { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; padding: 0; border: 0; border-radius: var(--chat-radius, 12px); background: rgba(255,255,255,.94); color: #475467; cursor: pointer;
      svg { width: 22px; height: 22px; fill: none; stroke: currentColor; stroke-width: 1.6; }
      &:hover { color: #246de0; background: #f2f7ff; }
      &:focus-visible { outline: 2px solid #4286f5; }
    }
    .chat-main { flex: 1 1 0; min-width: 0; overflow: hidden; border-radius: var(--chat-radius, 12px); margin-left: 0; transition: margin-left 240ms cubic-bezier(.2,.8,.2,1); }
    .chat-main > .chat-header { padding-left: 16px; }
    &.is-page {
      .workbench-tools { top: 68px; }
      .chat-main > .chat-header { height: 64px; min-height: 64px; box-sizing: border-box; }
      .page-tools-space { height: 52px; flex: 0 0 52px; }
      ::v-deep .workflow-chat.compact-header .messages { padding-top: 16px; }
    }
    .workbench-page-avatar { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; flex: 0 0 40px; color: #fff; background: #07c160; border-radius: 12px; }

    .group-language-placeholder { display: inline-flex; cursor: not-allowed; }
    .group-language-toggle { opacity: .5; pointer-events: none; }
    .workbench-language { flex: 0 0 44px; border-radius: var(--chat-radius, 12px); background: rgba(255,255,255,.94); color: #475467; }
    .chat-main ::v-deep .workflow-chat:not(.compact-header) .heading { padding-left: 100px; }
    .chat-sidebar { position: absolute; z-index: 3; inset: 0 auto 0 0; max-width: calc(100% - 48px); transform: translate3d(-100%,0,0); visibility: hidden; transition: transform 240ms cubic-bezier(.2,.8,.2,1), visibility 240ms; }
    &.is-wide .chat-sidebar, &.list-open .chat-sidebar { transform: translate3d(0,0,0); visibility: visible; }
    &.is-wide .chat-main { margin-left: var(--chat-rail-space, 0px); }
    &:not(.is-wide) .sidebar-header { padding-top: 56px; }
    .list-scrim { position: absolute; z-index: 2; inset: 0; width: 100%; border: 0; background: rgba(15,23,42,.16); }
    &.is-mobile {
      .mobile-tools-space { height: 52px; flex: 0 0 52px; }
      touch-action: pan-y;
      .chat-container { position: relative; }
      .chat-sidebar { inset: 0; width: 100%; max-width: none; transform: none; visibility: visible; border-radius: var(--chat-radius, 12px); }
      .sidebar-header { padding: 12px; ::v-deep .el-input__inner { min-height: 44px; font-size: 16px; } }
      .chat-main { position: absolute; z-index: 4; inset: 0; display: flex; flex-direction: column; margin-left: 0; border-radius: var(--chat-radius, 12px); background: #fff; transform: translate3d(100%,0,0); transition: transform 240ms cubic-bezier(.2,.8,.2,1); pointer-events: none; }
      &.mobile-detail .chat-main { transform: translate3d(0,0,0); pointer-events: auto; box-shadow: -8px 0 24px rgba(15,23,42,.12); }
      &.is-pane-dragging .chat-main { transition: none; }
      .workbench-tools { top: 4px; left: 6px; right: auto; z-index: 5; transition: none; }
    }
    @media (prefers-reduced-motion: reduce) { .chat-sidebar, .chat-main, .workbench-tools { transition: none !important; } }
  }
  // 高度由个人中心右侧视口统一分配，避免按浏览器高度重复计算后越过父级底边。
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;

  .workflow-inbox-error {
    padding: 8px 12px;
    color: #b54708;
    font-size: 13px;
  }

  .chat-container {
    flex: 1 1 auto;
    display: flex;
    min-height: 0;
    height: auto;
    border: 1px solid #e4e7ed;
    border-radius: 16px;
    overflow: hidden;
    background: #fff;
  }


}

/* 左侧会话列表 */
.chat-sidebar {
  width: 280px;
  border-right: 1px solid #e4e7ed;
  border-radius: 16px 0 0 16px;
  display: flex;
  min-height: 0;
  overflow: hidden;
  flex-direction: column;
  background: rgb(255, 255, 255);

  .inbox-channels {
    display: flex;
    flex: 0 0 auto;
    gap: 4px;
    padding: 0 12px 8px;
    button {
      display: inline-flex; align-items: center; justify-content: center; gap: 6px;
      flex: 1 1 0; min-width: 44px; min-height: 44px; padding: 0 12px;
      border: 0; border-radius: 8px; background: #f1f5f9; color: #667085;
      font: inherit; font-size: 14px; white-space: nowrap; cursor: pointer;
      &.active { background: #e6f0fe; color: #246de0; font-weight: 600; }
      &:focus-visible { outline: 2px solid #4286f5; outline-offset: -2px; }
    }
    .channel-unread { min-width: 18px; padding: 1px 5px; box-sizing: border-box; border-radius: 10px; font-size: 11px; color: #fff; background: #e53935; }
  }


  .conversation-list {
    flex: 1 1 0;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;

    .conversation-swipe {
      position: relative;
      width: 100%;
      background: #fff;
    }

    .conversation-swipe::after {
      content: "";
      position: absolute;
      z-index: 2;
      bottom: 0;
      left: 18px;
      right: 18px;
      height: 1px;
      background: #eef2f7;
      pointer-events: none;
    }

    .conversation-delete-action,
    .conversation-forget-action {
      width: 72px;
      padding: 6px 5px;
      box-sizing: border-box;
      white-space: normal;
      overflow-wrap: anywhere;
      height: 100%;
      border: 0;
      color: #fff;
      background: #ff4d4f;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: background-color 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        background: #ff7875;
      }

      &:active {
        background: #d9363e;
      }

      &:focus-visible {
        outline: 0;
        box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.72);
      }

      &:disabled {
        cursor: not-allowed;
        background: #fecdca;
        color: #b42318;
      }
    }

    .conversation-forget-action {
      background: #246de0;

      &:hover {
        background: #4286f5;
      }

      &:active {
        background: #1e5bb8;
      }

      &:disabled {
        color: #667085;
        background: #e6f0fe;
      }
    }

    .conversation-item {
      display: flex;
      align-items: center;
      box-sizing: border-box;
      width: 100%;
      padding: 12px 10px 12px 12px;
      border-radius: 6px;
      cursor: pointer;
      background: rgb(255, 255, 255);
      transition: background-color 180ms ease, color 180ms ease;
      -webkit-touch-callout: none;
      user-select: none;
      &.is-pinned:not(.active) { background: #f1f5f9; }
      .conv-pin { flex-shrink: 0; color: #94a3b8; margin-left: 6px; }
      &.active .conv-pin { color: #fff; }



      &:hover {
        background: #eaf2ff;
      }

      &.active {
        background: rgb(69, 143, 248);

        .conv-name,
        .conv-time,
        .conv-last-msg {
          color: #fff;
        }
        .conv-last-msg,
        .conv-time {
          opacity: 0.82;
        }

      }



      .conv-avatar {
        width: 44px;
        height: 44px;
        border-radius: 6px;
        overflow: hidden;
        margin-right: 10px;
        flex-shrink: 0;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .conv-avatar-placeholder {
          width: 100%;
          height: 100%;
          background: #07c160;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          font-weight: 600;
        }
      }

      .conv-info {
        flex: 1;
        min-width: 0;

        .conv-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;

          .conv-name {
            font-size: 14px;
            font-weight: 500;
            color: #333;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex: 1;
          }

          .conv-kind {
            margin-left: 6px;
            padding: 1px 5px;
            border-radius: 3px;
            background: #e4f4ec;
            color: #16845c;
            font-size: 10px;
            white-space: nowrap;
          }

          .conv-time {
            font-size: 11px;
            color: #999;
            margin-left: 8px;
            flex-shrink: 0;
          }
        }

        .conv-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;

          .conv-last-msg {
            font-size: 12px;
            color: #999;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex: 1;
          }

          .conv-unread {
            font-size: 10px;
            color: #fff;
            background: #e53935;
            border-radius: 10px;
            padding: 1px 6px;
            margin-left: 8px;
            min-width: 18px;
            text-align: center;
            flex-shrink: 0;
          }
        }
      }

      &.active {
        .conv-info {
          .conv-top {
            .conv-name {
              color: #fff;
            }

            .conv-kind {
              background: rgba(255, 255, 255, 0.2);
              color: #fff;
            }

            .conv-time {
              color: rgba(255, 255, 255, 0.78);
              opacity: 1;
            }
          }

          .conv-bottom .conv-last-msg {
            color: rgba(255, 255, 255, 0.72);
            opacity: 1;
          }
        }
      }
    }

    .no-conversation {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 200px;
      color: #999;
      font-size: 14px;
    }
  }
}

/* 右侧聊天区域 */
.chat-main {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-radius: 0 16px 16px 0;

  .formal-sync-error {
    padding: 8px 16px;
    color: #b54708;
    background: #fffaeb;
    font-size: 13px;
  }

  .chat-header {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 52px;
    padding: 0 20px;
    border-bottom: 1px solid #e4e7ed;
    font-size: 16px;
    font-weight: 600;
    color: #333;
    background: #fff;
    flex-shrink: 0;

    .chat-title {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .header-language-control { margin-left: auto; }
    .peer-presence {
      color: #98a2b3;
      font-size: 12px;
      font-weight: 500;
      &.online {
        color: #246de0;
      }
    }
  }

  .chat-messages {
    flex: 1;
    min-width: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 20px;
    background: rgb(255, 255, 255);

    .message-time-divider {
      margin: 18px 0 14px;
      color: #98a2b3;
      font-size: 12px;
      line-height: 18px;
      text-align: center;
    }

    .message-row {
      display: flex;
      align-items: flex-start;
      margin-bottom: 20px;

      .msg-bubble {
        max-width: 55%;
        padding: 10px 14px;
        border-radius: 6px;
        font-size: 14px;
        line-height: 1.5;
        word-break: break-all;
        position: relative;

        .msg-text {
          white-space: pre-wrap;
        }
      }

      .bubble-other {
        background: rgb(246, 246, 247);
        color: #333;
        border-top-left-radius: 2px;
      }

      .bubble-self {
        background: rgb(201, 231, 255);
        color: #1f2937;
        border-top-right-radius: 2px;
      }
    }

    .message-other {
      flex-direction: row;
    }

    .message-self {
      flex-direction: row-reverse;
    }
  }

  .chat-input-area {
    display: flex;
    height: 20%;
    flex: 0 0 20%;
    min-height: 0;
    flex-direction: column;
    border-top: 1px solid #e4e7ed;
    background: #fff;

    .input-toolbar {
      display: flex;
      gap: 0;
      padding: 8px 20px 0;
      align-items: center;

      .tool-icon {
        font-size: 20px;
        color: #666;
        cursor: pointer;

        &:hover {
          color: #07c160;
        }
      }
    }

    .chat-textarea {
      flex: 1;
      min-height: 0;
      padding: 0 20px;

      :deep(.el-textarea__inner) {
        height: 100%;
        min-height: 0 !important;
        border: none;
        resize: none;
        font-size: 14px;
        padding: 8px 0;
        box-shadow: none;

        &:focus {
          box-shadow: none;
        }
      }
    }

    .input-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      padding: 8px 20px 12px;

      .input-tip {
        flex: 1;
        min-width: 0;
        font-size: 12px;
        color: #999;
      }
    }
  }

  .chat-placeholder {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;

    .placeholder-content {
      text-align: center;
      color: #bbb;

      .placeholder-icon {
        font-size: 64px;
        display: block;
        margin-bottom: 16px;
      }

      p {
        font-size: 14px;
        margin: 0;
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .message-center {
    .conversation-context-delete,
    .conversation-context-forget,
    .conversation-delete-action,
    .conversation-forget-action,
    .conversation-item {
      transition: none;
    }
  }
}
.conversation-context-menu {
  position: fixed;
  z-index: 2100;
  max-width: calc(100vw - 16px);
  box-sizing: border-box;
  padding: 4px;
  border: 1px solid #dde3ee;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);

  .conversation-context-delete,
  .conversation-context-forget,
  .conversation-context-pin {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 44px;
    padding: 0 12px;
    border: 0;
    border-radius: 6px;
    color: #b42318;
    background: #fff;
    cursor: pointer;
    font-size: 14px;
    text-align: left;
    white-space: nowrap;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      background: #fef3f2;
    }

    &:active {
      background: #fee4e2;
    }

    &:focus-visible {
      outline: 0;
      box-shadow: inset 0 0 0 2px #f97066;
    }

    &:disabled {
      cursor: not-allowed;
      color: #b42318;
      background: #fecdca;
    }
  }

  .conversation-context-forget,
  .conversation-context-pin {
    color: #246de0;

    &:hover {
      background: #f2f7ff;
    }

    &:active {
      background: #e6f0fe;
    }

    &:focus-visible {
      box-shadow: inset 0 0 0 2px #9bc2ff;
    }

    &:disabled {
      color: #98a2b3;
      background: #f8fafc;
    }
  }
}
@media (prefers-reduced-motion: reduce) { .conversation-context-menu button { transition: none; } }
</style>
